import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "@arcadeai/design-system/components/ui/atoms/avatar";
import { Badge } from "@arcadeai/design-system/components/ui/atoms/badge";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@arcadeai/design-system/components/ui/atoms/hover-card";
import {
  MCP_OAUTH2_PROVIDER,
  OAUTH2_PROVIDER,
} from "@arcadeai/design-system/metadata/oauth-providers";
import { findPrebuiltProviderById } from "@arcadeai/design-system/metadata/utils";
import { Link } from "@tanstack/react-router";
import type { ColumnDef } from "@tanstack/react-table";
import type { ConnectedAccountItem } from "@/app/oauth/types";
import { DataTableColumnHeader } from "@/components/custom/table/data-table-column-header";
import { DeleteAction } from "./delete-action";

const getStatusVariant = (status: string) => {
  switch (status) {
    case "active":
      return "success";
    case "revoked":
      return "destructive";
    default:
      return "secondary";
  }
};

const baseColumns: ColumnDef<ConnectedAccountItem>[] = [
  {
    accessorKey: "name",
    id: "name",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Name" />
    ),
    cell: ({ row }) => {
      const account = row.original;
      return (
        <div className="flex items-center gap-3">
          {/* biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code */}
          {!!(account.provider_user_info?.picture as string) && (
            <Avatar>
              <AvatarImage
                // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                alt={account.provider_user_info?.name as string}
                // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
                src={account.provider_user_info?.picture as string}
              />
              <AvatarFallback>
                {/* biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code */}
                {(account.provider_user_info?.name as string)?.slice(0, 2) ??
                  account.user_id}
              </AvatarFallback>
            </Avatar>
          )}
          <div>
            <div className="font-medium">
              {/* biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code */}
              {(account.provider_user_info?.name as string) ?? account.user_id}
            </div>
            {/* biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code */}
            {!!(account.provider_user_info?.email as string) && (
              <div className="text-muted-foreground text-sm">
                {/* biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code */}
                {account.provider_user_info?.email as string}
              </div>
            )}
          </div>
        </div>
      );
    },
    filterFn: (row, _, value) => {
      // We either search by name or user_id
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const name = row.original.provider_user_info?.name as string | undefined;
      const userId = row.original.user_id;
      return (
        (name?.toLowerCase().includes(value.toLowerCase()) ?? false) ||
        userId.toLowerCase().includes(value.toLowerCase())
      );
    },
  },
  {
    accessorKey: "user_id",
    id: "user_id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="User ID" />
    ),
    cell: ({ row }) => (
      <div className="text-muted-foreground text-sm">
        {row.original.user_id}
      </div>
    ),
    filterFn: (row, _, value) => {
      if (Array.isArray(value)) {
        return value.includes(row.original.user_id);
      }
      return row.original.user_id.toLowerCase().includes(value.toLowerCase());
    },
  },
  {
    accessorKey: "provider_id",
    id: "provider_id",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Provider" />
    ),
    cell: ({ row }) => {
      const baseDescriptor =
        findPrebuiltProviderById(row.original.provider_id) ?? OAUTH2_PROVIDER;

      const descriptor =
        row.original.provider_type === "mcp_oauth2"
          ? MCP_OAUTH2_PROVIDER
          : baseDescriptor;

      return (
        <div className="flex items-center gap-4">
          <Link
            from="/orgs/$organizationId/projects/$projectId/auth/connected-users"
            to="."
          >
            <Badge className="flex items-center gap-3" variant="secondary">
              <descriptor.icon className="h-3 w-3" />
              {row.original.provider_id}
            </Badge>
          </Link>
        </div>
      );
    },
    filterFn: (row, _, value) => {
      if (Array.isArray(value)) {
        return value.includes(row.original.provider_id);
      }
      return row.original.provider_id
        .toLowerCase()
        .includes(value.toLowerCase());
    },
  },
  {
    accessorKey: "scopes",
    id: "scopes",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Scopes" />
    ),
    cell: ({ row }) => {
      const scopes = row.original.scopes;
      const numScopes = scopes.length;

      // No scopes case
      if (numScopes === 0) {
        return <div className="text-muted-foreground text-sm">No scopes</div>;
      }

      // Single scope case
      if (numScopes === 1) {
        return (
          <Badge className="flex items-center gap-2" variant="secondary">
            {scopes[0]}
          </Badge>
        );
      }

      // Multiple scopes case
      return (
        <HoverCard>
          <HoverCardTrigger
            render={<div className="flex cursor-pointer items-center gap-2" />}
          >
            <Badge className="flex items-center gap-2" variant="secondary">
              {scopes[0]}
            </Badge>
            <Badge
              className="flex items-center gap-1 text-xs"
              variant="default"
            >
              +{numScopes - 1}
            </Badge>
          </HoverCardTrigger>
          <HoverCardContent className="w-auto max-w-md p-4">
            <div className="space-y-2">
              <h4 className="mb-4 font-semibold text-sm">
                All Scopes ({numScopes})
              </h4>
              <div className="flex flex-wrap gap-2">
                {scopes.map((scope) => (
                  <Badge className="text-xs" key={scope} variant="secondary">
                    {scope}
                  </Badge>
                ))}
              </div>
            </div>
          </HoverCardContent>
        </HoverCard>
      );
    },
  },
  {
    accessorKey: "connection_status",
    id: "connection_status",
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title="Status" />
    ),
    cell: ({ row }) => {
      // biome-ignore lint/plugin/no-type-assertion: existing type assertion; prefer type guards for new code
      const status = row.getValue("connection_status") as string;
      return (
        <Badge variant={getStatusVariant(status)}>
          {status.replace("_", " ")}
        </Badge>
      );
    },
    filterFn: (row, id, value) => value.includes(row.getValue(id)),
  },
];

const actionsColumn: ColumnDef<ConnectedAccountItem> = {
  id: "actions",
  header: ({ column }) => (
    <DataTableColumnHeader column={column} title="Actions" />
  ),
  cell: ({ row }) => <DeleteAction account={row.original} />,
};

// Revoke is the only row action, so omit the column entirely when the caller
// cannot revoke connections rather than render an empty column.
export const getConnectedAccountsColumns = (
  canRevoke: boolean
): ColumnDef<ConnectedAccountItem>[] =>
  canRevoke ? [...baseColumns, actionsColumn] : baseColumns;
